import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CaasTeam, CaasTeamCreateRequest, CaasTeamUpdateRequest } from '../../../../types';
import { createTeam, updateTeam } from '../../../../services/api/endpoints/caas';
import { useAuth } from '../../../../contexts/AuthContext';
import { useBuildings } from '../../hooks/useBuildings';
import BuildingFormModal from '../buildings/BuildingFormModal';

interface TeamFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  team?: CaasTeam | null; // If provided, edit mode; otherwise create mode
  clientId?: string; // Required for create mode, pre-fills client relationship
}

const TeamFormModal: React.FC<TeamFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  team,
  clientId,
}) => {
  const { user } = useAuth();
  const { buildings, isLoading: buildingsLoading, refresh: refreshBuildings } = useBuildings();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    building_id: '',
    domains: [] as string[],
  });
  const [isBuildingModalOpen, setIsBuildingModalOpen] = useState(false);
  const [currentDomain, setCurrentDomain] = useState('');
  const [domainError, setDomainError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const isEditMode = !!team;

  // Initialize form data when modal opens or team changes
  useEffect(() => {
    if (open) {
      if (team) {
        setFormData({
          name: team.name,
          address: team.address,
          building_id: team.building_id || '',
          domains: team.domains || [],
        });
      } else {
        setFormData({
          name: '',
          address: '',
          building_id: '',
          domains: [],
        });
      }
      setCurrentDomain('');
      setDomainError(null);
      setErrors({});
      setApiError(null);
    }
  }, [open, team]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const isValidDomain = (domain: string): boolean => {
    // Basic domain validation - alphanumeric, dots, hyphens
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?)*\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain.trim());
  };

  const handleAddDomain = () => {
    const trimmedDomain = currentDomain.trim().toLowerCase();
    if (!trimmedDomain) return;
    
    if (!isValidDomain(trimmedDomain)) {
      setDomainError('Invalid domain format. Use format like "example.com"');
      return;
    }
    
    if (formData.domains.includes(trimmedDomain)) {
      setDomainError('This domain has already been added');
      return;
    }
    
    setFormData({
      ...formData,
      domains: [...formData.domains, trimmedDomain]
    });
    setCurrentDomain('');
    setDomainError(null);
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setFormData({
      ...formData,
      domains: formData.domains.filter(d => d !== domainToRemove)
    });
  };

  const handleDomainKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.length > 500) {
      newErrors.address = 'Address must be 500 characters or less';
    }


    // Validate clientId for create mode
    if (!isEditMode && !clientId) {
      setApiError('Client ID is required to create a team');
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBuildingCreated = async (buildingId: string) => {
    // Close building modal
    setIsBuildingModalOpen(false);
    
    // Refresh buildings list
    await refreshBuildings();
    
    // Auto-select the newly created building
    setFormData({
      ...formData,
      building_id: buildingId,
    });
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    if (!user?.token) {
      setApiError('No authentication token available');
      return;
    }

    try {
      setIsSaving(true);
      setApiError(null);

      if (isEditMode && team) {
        const payload: CaasTeamUpdateRequest = {
          name: formData.name.trim(),
          address: formData.address.trim(),
          building_id: formData.building_id ? formData.building_id : null,
          caas_client_id: team.caas_client_id,
          domains: formData.domains,
        };
        await updateTeam(user.token, team.id, payload);
      } else if (clientId) {
        const payload: CaasTeamCreateRequest = {
          name: formData.name.trim(),
          address: formData.address.trim(),
          building_id: formData.building_id ? formData.building_id : null,
          caas_client_id: clientId,
          domains: formData.domains.length > 0 ? formData.domains : undefined,
        };
        await createTeam(user.token, payload);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error saving team:', err);
      setApiError(err.message || 'Failed to save team');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="team-form-modal-title"
      >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '600px' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography id="team-form-modal-title" variant="h6" component="h2">
            {isEditMode ? 'Edit Team' : 'Create Team'}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Required'}
            disabled={isSaving}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            error={!!errors.address}
            helperText={errors.address || 'Required'}
            disabled={isSaving}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="building-select-label">Building</InputLabel>
            <Select
              labelId="building-select-label"
              value={formData.building_id}
              label="Building"
              onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
              disabled={isSaving || buildingsLoading}
            >
              <MenuItem 
                value="__create_new__"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsBuildingModalOpen(true);
                }}
                sx={{ 
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                + Create New Building
              </MenuItem>
              <MenuItem value="">None</MenuItem>
              {buildings.map((building) => (
                <MenuItem key={building.id} value={building.id}>
                  {building.name} - {building.address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Domains Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Allowed Email Domains (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Users with email addresses from these domains can access this team.
            </Typography>
            
            {/* Input field with Add button */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., example.com"
                value={currentDomain}
                onChange={(e) => setCurrentDomain(e.target.value)}
                onKeyPress={handleDomainKeyPress}
                disabled={isSaving}
                error={!!domainError}
                helperText={domainError || 'Press Enter or click Add'}
              />
              <Button
                variant="outlined"
                onClick={handleAddDomain}
                disabled={isSaving || !currentDomain.trim()}
                sx={{ minWidth: '80px' }}
              >
                Add
              </Button>
            </Box>
            
            {/* Display domains as chips */}
            {formData.domains.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {formData.domains.map((domain) => (
                  <Chip
                    key={domain}
                    label={domain}
                    onDelete={() => handleRemoveDomain(domain)}
                    disabled={isSaving}
                    size="small"
                  />
                ))}
              </Box>
            )}
            
            {formData.domains.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No domains added. Team will not restrict access by email domain.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : isEditMode ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Box>
      </Modal>
      
      {/* Building Creation Modal - Outside main modal */}
      <BuildingFormModal
        open={isBuildingModalOpen}
        onClose={() => setIsBuildingModalOpen(false)}
        onSuccess={handleBuildingCreated}
      />
    </>
  );
};

export default TeamFormModal;
